/**
 * Mock Data Service
 * Provides realistic mock data for development and demos
 *
 * This module is controlled by the REACT_APP_USE_MOCK_DATA environment variable.
 * When true, all API calls return mock data instead of hitting the backend.
 */

export const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === 'true';

// Helper to generate dates relative to today
const daysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString();
};

// Mock user onboarding state
export const mockOnboardingState = {
  hasUploadedHistoricalEstimates: true,
  hasCreatedFirstEstimate: true,
  hasAdjustedPricingRules: false,
};

// Mock pricing health metrics (last 30 days)
export const mockPricingHealth = {
  quotesCreated: {
    value: 12,
    trend: 'up', // 'up', 'down', 'stable'
    trendPercent: 15,
  },
  avgAdjustment: {
    value: 3.8,
    trend: 'stable',
    trendPercent: 0.5,
    tooltip: 'You tend to price slightly higher than AI',
  },
  acceptanceRate: {
    value: 67,
    trend: 'up',
    trendPercent: 8,
    tooltip: 'Based on quotes with known outcomes',
  },
  highRiskQuotes: {
    value: 2,
    trend: null,
    items: ['Q-2024-089', 'Q-2024-091'],
  },
};

// Mock recent quotes
export const mockRecentQuotes = [
  {
    id: 'Q-2024-092',
    jobName: 'Whole Foods Market - Eastgate',
    customer: 'Whole Foods Market Inc.',
    jobType: 'Retail',
    squareFootage: 45000,
    location: 'Cincinnati, OH',
    aiSuggestedPrice: 4250000,
    finalPrice: 4385000,
    confidenceScore: 0.87,
    riskLevel: 'low',
    status: 'draft',
    createdAt: daysAgo(1),
    similarJobsCount: 8,
  },
  {
    id: 'Q-2024-091',
    jobName: 'Home Depot Distribution Center',
    customer: 'The Home Depot',
    jobType: 'Warehouse',
    squareFootage: 125000,
    location: 'Columbus, OH',
    aiSuggestedPrice: 8750000,
    finalPrice: null,
    confidenceScore: 0.62,
    riskLevel: 'high',
    status: 'pending_review',
    createdAt: daysAgo(3),
    similarJobsCount: 3,
    riskReason: 'Limited similar jobs in database, unusual site conditions',
  },
  {
    id: 'Q-2024-090',
    jobName: 'Kroger Marketplace',
    customer: 'The Kroger Co.',
    jobType: 'Retail',
    squareFootage: 62000,
    location: 'Dayton, OH',
    aiSuggestedPrice: 5890000,
    finalPrice: 6100000,
    confidenceScore: 0.91,
    riskLevel: 'low',
    status: 'sent',
    createdAt: daysAgo(5),
    similarJobsCount: 12,
  },
  {
    id: 'Q-2024-089',
    jobName: 'Medical Office Building - Mercy Health',
    customer: 'Mercy Health Partners',
    jobType: 'Medical Office',
    squareFootage: 35000,
    location: 'Cincinnati, OH',
    aiSuggestedPrice: 5200000,
    finalPrice: 4850000,
    confidenceScore: 0.68,
    riskLevel: 'medium',
    status: 'sent',
    createdAt: daysAgo(8),
    similarJobsCount: 5,
    riskReason: 'Medical facility pricing variance is typically higher',
  },
  {
    id: 'Q-2024-088',
    jobName: 'Target Store Renovation',
    customer: 'Target Corporation',
    jobType: 'Retail',
    squareFootage: 140000,
    location: 'Louisville, KY',
    aiSuggestedPrice: 3200000,
    finalPrice: 3150000,
    confidenceScore: 0.94,
    riskLevel: 'low',
    status: 'accepted',
    createdAt: daysAgo(12),
    similarJobsCount: 15,
  },
  {
    id: 'Q-2024-087',
    jobName: 'Amazon Fulfillment Center',
    customer: 'Amazon.com Services',
    jobType: 'Warehouse',
    squareFootage: 850000,
    location: 'Hebron, KY',
    aiSuggestedPrice: 45000000,
    finalPrice: 47500000,
    confidenceScore: 0.85,
    riskLevel: 'low',
    status: 'accepted',
    createdAt: daysAgo(18),
    similarJobsCount: 4,
  },
];

// Mock pricing signals/warnings
export const mockPricingSignals = [
  {
    id: 'signal-1',
    type: 'warning',
    severity: 'medium',
    message: 'Recent quotes trend 3.8% higher than historical averages',
    details: 'Your final prices have been consistently higher than AI suggestions over the past 30 days.',
    actionable: true,
    actionText: 'Review pricing rules',
  },
  {
    id: 'signal-2',
    type: 'insight',
    severity: 'info',
    message: 'Material-heavy jobs showing higher variance',
    details: 'Quotes with significant steel and concrete components have 12% more variability in final pricing.',
    actionable: false,
  },
  {
    id: 'signal-3',
    type: 'alert',
    severity: 'high',
    message: '2 quotes flagged as higher risk than usual',
    details: 'Q-2024-091 and Q-2024-089 have confidence scores below 70%.',
    actionable: true,
    actionText: 'Review high-risk quotes',
    linkedQuotes: ['Q-2024-091', 'Q-2024-089'],
  },
  {
    id: 'signal-4',
    type: 'insight',
    severity: 'info',
    message: 'Prevailing wage projects priced 18% higher on average',
    details: 'Government and public works projects in your area consistently require higher labor cost adjustments.',
    actionable: false,
  },
];

// Full historical jobs list (for Jobs page)
export const mockHistoricalJobs = [
  {
    id: 'HJ-2023-045',
    projectName: 'Giant Eagle Supermarket - Solon',
    customer: 'Giant Eagle Inc.',
    jobType: 'Retail',
    squareFootage: 52000,
    location: 'Solon, OH',
    completedDate: '2023-08-15',
    finalCost: 5100000,
    costPerSqFt: 98.08,
    outcome: 'profitable',
    margin: 12.5,
    timesReferenced: 8,
    costBreakdown: {
      sitework: 408000,
      concrete: 612000,
      masonry: 255000,
      steel: 765000,
      carpentry: 357000,
      roofing: 306000,
      interiorFinishes: 918000,
      mep: 1122000,
      generalConditions: 357000,
    },
    notes: 'Standard grocery store build. Good relationship with owner.',
    uploadedAt: '2023-09-01',
    source: 'csv_import',
  },
  {
    id: 'HJ-2023-038',
    projectName: 'Costco Warehouse - Mason',
    customer: 'Costco Wholesale',
    jobType: 'Retail',
    squareFootage: 148000,
    location: 'Mason, OH',
    completedDate: '2023-06-22',
    finalCost: 12800000,
    costPerSqFt: 86.49,
    outcome: 'profitable',
    margin: 15.2,
    timesReferenced: 6,
    costBreakdown: {
      sitework: 1024000,
      concrete: 1536000,
      masonry: 640000,
      steel: 1920000,
      carpentry: 896000,
      roofing: 768000,
      interiorFinishes: 2304000,
      mep: 2816000,
      generalConditions: 896000,
    },
    notes: 'Large format retail. Prevailing wage project.',
    uploadedAt: '2023-07-15',
    source: 'csv_import',
  },
  {
    id: 'HJ-2022-089',
    projectName: 'FedEx Ground Hub',
    customer: 'FedEx Ground',
    jobType: 'Warehouse',
    squareFootage: 280000,
    location: 'Erlanger, KY',
    completedDate: '2022-11-30',
    finalCost: 18500000,
    costPerSqFt: 66.07,
    outcome: 'profitable',
    margin: 11.8,
    timesReferenced: 5,
    costBreakdown: {
      sitework: 1850000,
      concrete: 2775000,
      masonry: 925000,
      steel: 3330000,
      carpentry: 1110000,
      roofing: 1480000,
      interiorFinishes: 2590000,
      mep: 3145000,
      generalConditions: 1295000,
    },
    notes: 'Distribution center with extensive loading docks.',
    uploadedAt: '2023-01-10',
    source: 'csv_import',
  },
  {
    id: 'HJ-2023-012',
    projectName: 'Meijer Supercenter',
    customer: 'Meijer Inc.',
    jobType: 'Retail',
    squareFootage: 190000,
    location: 'Florence, KY',
    completedDate: '2023-03-18',
    finalCost: 16200000,
    costPerSqFt: 85.26,
    outcome: 'profitable',
    margin: 14.1,
    timesReferenced: 4,
    costBreakdown: {
      sitework: 1296000,
      concrete: 1944000,
      masonry: 810000,
      steel: 2430000,
      carpentry: 1134000,
      roofing: 972000,
      interiorFinishes: 2916000,
      mep: 3564000,
      generalConditions: 1134000,
    },
    notes: 'Full supercenter with grocery and general merchandise.',
    uploadedAt: '2023-04-01',
    source: 'csv_import',
  },
  {
    id: 'HJ-2022-067',
    projectName: 'Medical Office Complex - TriHealth',
    customer: 'TriHealth Inc.',
    jobType: 'Medical Office',
    squareFootage: 42000,
    location: 'Cincinnati, OH',
    completedDate: '2022-09-08',
    finalCost: 6300000,
    costPerSqFt: 150.00,
    outcome: 'marginal',
    margin: 5.2,
    timesReferenced: 3,
    costBreakdown: {
      sitework: 378000,
      concrete: 504000,
      masonry: 315000,
      steel: 630000,
      carpentry: 441000,
      roofing: 315000,
      interiorFinishes: 1260000,
      mep: 1890000,
      generalConditions: 567000,
    },
    notes: 'Complex MEP requirements for medical equipment. Change orders impacted margin.',
    uploadedAt: '2022-10-01',
    source: 'csv_import',
  },
  {
    id: 'HJ-2023-067',
    projectName: 'Kroger Fuel Center',
    customer: 'The Kroger Co.',
    jobType: 'Retail',
    squareFootage: 3500,
    location: 'West Chester, OH',
    completedDate: '2023-10-12',
    finalCost: 850000,
    costPerSqFt: 242.86,
    outcome: 'profitable',
    margin: 18.5,
    timesReferenced: 2,
    costBreakdown: {
      sitework: 170000,
      concrete: 127500,
      masonry: 42500,
      steel: 85000,
      carpentry: 59500,
      roofing: 42500,
      interiorFinishes: 127500,
      mep: 153000,
      generalConditions: 42500,
    },
    notes: 'Small fuel center kiosk. Quick turnaround project.',
    uploadedAt: '2023-11-01',
    source: 'csv_import',
  },
  {
    id: 'HJ-2022-045',
    projectName: 'Amazon Delivery Station',
    customer: 'Amazon.com Services',
    jobType: 'Warehouse',
    squareFootage: 85000,
    location: 'Fairfield, OH',
    completedDate: '2022-05-20',
    finalCost: 5950000,
    costPerSqFt: 70.00,
    outcome: 'profitable',
    margin: 13.2,
    timesReferenced: 7,
    costBreakdown: {
      sitework: 595000,
      concrete: 892500,
      masonry: 297500,
      steel: 1071000,
      carpentry: 416500,
      roofing: 476000,
      interiorFinishes: 833000,
      mep: 1011500,
      generalConditions: 357000,
    },
    notes: 'Last-mile delivery station. Aggressive schedule.',
    uploadedAt: '2022-06-15',
    source: 'csv_import',
  },
  {
    id: 'HJ-2023-089',
    projectName: 'Hilton Garden Inn',
    customer: 'Hilton Hotels',
    jobType: 'Hospitality',
    squareFootage: 75000,
    location: 'Blue Ash, OH',
    completedDate: '2023-12-01',
    finalCost: 11250000,
    costPerSqFt: 150.00,
    outcome: 'profitable',
    margin: 10.8,
    timesReferenced: 1,
    costBreakdown: {
      sitework: 675000,
      concrete: 1012500,
      masonry: 562500,
      steel: 1350000,
      carpentry: 787500,
      roofing: 562500,
      interiorFinishes: 2700000,
      mep: 2812500,
      generalConditions: 787500,
    },
    notes: '120-room hotel. Interior finish heavy.',
    uploadedAt: '2023-12-15',
    source: 'csv_import',
  },
  {
    id: 'HJ-2022-023',
    projectName: 'Fifth Third Bank Branch',
    customer: 'Fifth Third Bank',
    jobType: 'Office',
    squareFootage: 8500,
    location: 'Hamilton, OH',
    completedDate: '2022-03-15',
    finalCost: 1700000,
    costPerSqFt: 200.00,
    outcome: 'profitable',
    margin: 16.4,
    timesReferenced: 4,
    costBreakdown: {
      sitework: 136000,
      concrete: 170000,
      masonry: 119000,
      steel: 170000,
      carpentry: 119000,
      roofing: 85000,
      interiorFinishes: 408000,
      mep: 391000,
      generalConditions: 102000,
    },
    notes: 'Bank branch with vault and drive-through.',
    uploadedAt: '2022-04-01',
    source: 'csv_import',
  },
  {
    id: 'HJ-2023-034',
    projectName: 'LA Fitness',
    customer: 'LA Fitness International',
    jobType: 'Retail',
    squareFootage: 38000,
    location: 'Kenwood, OH',
    completedDate: '2023-05-10',
    finalCost: 4180000,
    costPerSqFt: 110.00,
    outcome: 'profitable',
    margin: 11.5,
    timesReferenced: 3,
    costBreakdown: {
      sitework: 334400,
      concrete: 501600,
      masonry: 209000,
      steel: 627000,
      carpentry: 292600,
      roofing: 250800,
      interiorFinishes: 752400,
      mep: 920000,
      generalConditions: 292200,
    },
    notes: 'Fitness center with pool. Complex HVAC requirements.',
    uploadedAt: '2023-06-01',
    source: 'csv_import',
  },
];

// Mock similar/referenced jobs (recently used by AI for comparisons)
export const mockReferencedJobs = [
  {
    id: 'HJ-2023-045',
    projectName: 'Giant Eagle Supermarket - Solon',
    jobType: 'Retail',
    squareFootage: 52000,
    location: 'Solon, OH',
    completedDate: '2023-08-15',
    finalCost: 5100000,
    costPerSqFt: 98.08,
    outcome: 'profitable',
    timesReferenced: 8,
    margin: 12.5,
  },
  {
    id: 'HJ-2023-038',
    projectName: 'Costco Warehouse - Mason',
    jobType: 'Retail',
    squareFootage: 148000,
    location: 'Mason, OH',
    completedDate: '2023-06-22',
    finalCost: 12800000,
    costPerSqFt: 86.49,
    outcome: 'profitable',
    timesReferenced: 6,
    margin: 15.2,
  },
  {
    id: 'HJ-2022-089',
    projectName: 'FedEx Ground Hub',
    jobType: 'Warehouse',
    squareFootage: 280000,
    location: 'Erlanger, KY',
    completedDate: '2022-11-30',
    finalCost: 18500000,
    costPerSqFt: 66.07,
    outcome: 'profitable',
    timesReferenced: 5,
    margin: 11.8,
  },
  {
    id: 'HJ-2023-012',
    projectName: 'Meijer Supercenter',
    jobType: 'Retail',
    squareFootage: 190000,
    location: 'Florence, KY',
    completedDate: '2023-03-18',
    finalCost: 16200000,
    costPerSqFt: 85.26,
    outcome: 'profitable',
    timesReferenced: 4,
    margin: 14.1,
  },
  {
    id: 'HJ-2022-067',
    projectName: 'Medical Office Complex - TriHealth',
    jobType: 'Medical Office',
    squareFootage: 42000,
    location: 'Cincinnati, OH',
    completedDate: '2022-09-08',
    finalCost: 6300000,
    costPerSqFt: 150.00,
    outcome: 'marginal',
    timesReferenced: 3,
    margin: 5.2,
  },
];

// Mock dashboard stats summary
export const mockDashboardStats = {
  totalEstimates: 247,
  totalDraftEstimates: 12,
  avgConfidenceScore: 0.82,
  mostCommonJobType: 'Retail',
  lastUploadDate: daysAgo(14),
};

// Mock CSV uploads tracking
export const mockCsvUploads = [
  {
    id: 'upload-001',
    fileName: 'historical_jobs_2023.csv',
    uploadedAt: daysAgo(14),
    status: 'completed',
    rowsProcessed: 45,
    rowsSuccessful: 43,
    rowsFailed: 2,
    errors: [
      { row: 12, message: 'Invalid date format for completedDate' },
      { row: 28, message: 'Missing required field: squareFootage' },
    ],
  },
  {
    id: 'upload-002',
    fileName: 'jobs_q4_2022.csv',
    uploadedAt: daysAgo(45),
    status: 'completed',
    rowsProcessed: 28,
    rowsSuccessful: 28,
    rowsFailed: 0,
    errors: [],
  },
];

// Building types for filtering
export const jobTypes = [
  'Retail',
  'Warehouse',
  'Office',
  'Medical Office',
  'Hospitality',
  'Industrial',
  'Restaurant',
  'Education',
];

// Similarity metrics for pricing rules
export const mockSimilarityMetrics = [
  {
    id: 'job_type',
    displayName: 'Job Type Match',
    description: 'Prioritizes projects of the same building type (grocery, retail, warehouse, etc.)',
    defaultWeight: 20,
  },
  {
    id: 'square_footage',
    displayName: 'Square Footage Similarity',
    description: 'Prioritizes projects of similar size',
    defaultWeight: 20,
  },
  {
    id: 'construction_type',
    displayName: 'Construction Type Match',
    description: 'Prioritizes projects with same construction method (steel-frame, concrete, etc.)',
    defaultWeight: 15,
  },
  {
    id: 'location_distance',
    displayName: 'Location Distance',
    description: 'Prioritizes projects geographically close to the new project',
    defaultWeight: 15,
  },
  {
    id: 'site_condition',
    displayName: 'Site Condition Match',
    description: 'Prioritizes projects with similar site conditions (greenfield, brownfield, urban-infill)',
    defaultWeight: 10,
  },
  {
    id: 'timeline',
    displayName: 'Timeline Similarity',
    description: 'Prioritizes projects with similar duration and schedule requirements',
    defaultWeight: 10,
  },
  {
    id: 'vector_similarity',
    displayName: 'Description Similarity',
    description: 'Uses AI to match similar project descriptions and requirements',
    defaultWeight: 10,
  },
];

// Mock tenant pricing rules (stored per tenant)
let mockTenantPricingRules = {
  metricWeights: {
    job_type: 20,
    square_footage: 20,
    construction_type: 15,
    location_distance: 15,
    site_condition: 10,
    timeline: 10,
    vector_similarity: 10,
  },
  updatedAt: null,
  updatedBy: null,
};

// Mock industry default weights (set by admin)
let mockIndustryDefaultWeights = {
  commercial_construction: {
    metricWeights: {
      job_type: 20,
      square_footage: 20,
      construction_type: 15,
      location_distance: 15,
      site_condition: 10,
      timeline: 10,
      vector_similarity: 10,
    },
    updatedAt: '2025-12-01T10:00:00Z',
    updatedBy: 'admin',
  },
};

// Mock list of tenants with custom weights (for admin view)
const mockTenantsWithCustomWeights = [
  {
    tenantId: 'tenant-001',
    tenantName: 'ABC Construction',
    hasCustomWeights: true,
    updatedAt: '2026-01-05T14:30:00Z',
  },
  {
    tenantId: 'tenant-002',
    tenantName: 'BuildRight Inc.',
    hasCustomWeights: false,
    updatedAt: null,
  },
  {
    tenantId: 'tenant-003',
    tenantName: 'Premier Contractors',
    hasCustomWeights: true,
    updatedAt: '2026-01-02T09:15:00Z',
  },
];

// API wrapper functions that respect USE_MOCK_DATA flag
export const mockApiService = {
  /**
   * Get pricing health metrics for the dashboard
   */
  getPricingHealth: async () => {
    await simulateDelay();
    return mockPricingHealth;
  },

  /**
   * Get recent estimates for the dashboard
   */
  getRecentEstimates: async (limit = 6) => {
    await simulateDelay();
    // Return mock estimates data for dashboard
    return [
      {
        estimateId: 'est-001',
        projectName: 'Whole Foods Market - Oakley',
        clientName: 'Whole Foods Market Inc.',
        jobType: 'Retail',
        squareFootage: 45000,
        location: 'Cincinnati, OH',
        totalCost: 4500000,
        costPerSf: 100,
        estimateDate: daysAgo(2),
        status: 'complete',
      },
      {
        estimateId: 'est-002',
        projectName: 'Amazon Distribution Center',
        clientName: 'Amazon Logistics',
        jobType: 'Warehouse',
        squareFootage: 250000,
        location: 'Columbus, OH',
        totalCost: 22500000,
        costPerSf: 90,
        estimateDate: daysAgo(5),
        status: 'draft',
      },
      {
        estimateId: 'est-003',
        projectName: 'Medical Office Building',
        clientName: 'TriHealth Systems',
        jobType: 'Medical Office',
        squareFootage: 35000,
        location: 'Cincinnati, OH',
        totalCost: 5250000,
        costPerSf: 150,
        estimateDate: daysAgo(10),
        status: 'complete',
      },
      {
        estimateId: 'est-004',
        projectName: 'Target Store Renovation',
        clientName: 'Target Corporation',
        jobType: 'Retail',
        squareFootage: 140000,
        location: 'Louisville, KY',
        totalCost: 3200000,
        costPerSf: 22.86,
        estimateDate: daysAgo(15),
        status: 'complete',
      },
    ].slice(0, limit);
  },

  /**
   * Get pricing signals and warnings
   */
  getPricingSignals: async () => {
    await simulateDelay();
    return mockPricingSignals;
  },

  /**
   * Get recently referenced historical jobs
   */
  getReferencedJobs: async (limit = 5) => {
    await simulateDelay();
    return mockReferencedJobs.slice(0, limit);
  },

  /**
   * Get user onboarding state
   */
  getOnboardingState: async () => {
    await simulateDelay();
    return mockOnboardingState;
  },

  /**
   * Get dashboard stats
   */
  getDashboardStats: async () => {
    await simulateDelay();
    return mockDashboardStats;
  },

  /**
   * Get all dashboard data in one call
   */
  getDashboardData: async () => {
    await simulateDelay();
    const recentEstimates = await mockApiService.getRecentEstimates(6);
    return {
      pricingHealth: mockPricingHealth,
      recentEstimates,
      pricingSignals: mockPricingSignals,
      referencedJobs: mockReferencedJobs.slice(0, 5),
      onboardingState: mockOnboardingState,
      stats: mockDashboardStats,
    };
  },

  // Jobs API methods
  /**
   * Get list of historical jobs with optional filtering
   */
  getJobs: async ({ jobType, location, search, page = 1, limit = 10 } = {}) => {
    await simulateDelay();
    let jobs = [...mockHistoricalJobs];

    // Filter by building type
    if (jobType) {
      jobs = jobs.filter((j) => j.jobType === jobType);
    }

    // Filter by location (partial match)
    if (location) {
      jobs = jobs.filter((j) =>
        j.location.toLowerCase().includes(location.toLowerCase())
      );
    }

    // Search by project name or customer
    if (search) {
      const searchLower = search.toLowerCase();
      jobs = jobs.filter(
        (j) =>
          j.projectName.toLowerCase().includes(searchLower) ||
          j.customer.toLowerCase().includes(searchLower)
      );
    }

    // Pagination
    const total = jobs.length;
    const start = (page - 1) * limit;
    const paginatedJobs = jobs.slice(start, start + limit);

    return {
      jobs: paginatedJobs,
      total,
      page,
      limit,
      totalPages: Math.ceil(total / limit),
    };
  },

  /**
   * Get a single job by ID
   */
  getJobById: async (id) => {
    await simulateDelay();
    const job = mockHistoricalJobs.find((j) => j.id === id);
    if (!job) {
      throw new Error('Job not found');
    }
    return job;
  },

  /**
   * Get CSV upload history
   */
  getCsvUploads: async () => {
    await simulateDelay();
    return mockCsvUploads;
  },

  /**
   * Simulate CSV upload
   */
  uploadCsv: async (file) => {
    // Simulate longer delay for upload
    await simulateDelay(1000, 2000);

    // Generate mock response
    const mockRows = Math.floor(Math.random() * 20) + 5;
    return {
      id: `upload-${Date.now()}`,
      fileName: file.name,
      status: 'processing',
      rowsProcessed: mockRows,
      message: `Processing ${mockRows} rows...`,
    };
  },

  /**
   * Get upload status (for polling)
   */
  getUploadStatus: async (uploadId) => {
    await simulateDelay(500, 1000);

    // Simulate completed status
    return {
      id: uploadId,
      status: 'completed',
      rowsProcessed: 15,
      rowsSuccessful: 14,
      rowsFailed: 1,
      errors: [
        { row: 8, message: 'Invalid cost value' },
      ],
    };
  },

  /**
   * Get building types for filter dropdown
   */
  getJobTypes: async () => {
    await simulateDelay(100, 200);
    return jobTypes;
  },

  // Pricing Rules API methods

  /**
   * Get available similarity metrics and current weights (tenant view)
   */
  getPricingRules: async () => {
    await simulateDelay();
    return {
      availableMetrics: mockSimilarityMetrics,
      metricWeights: { ...mockTenantPricingRules.metricWeights },
      updatedAt: mockTenantPricingRules.updatedAt,
      updatedBy: mockTenantPricingRules.updatedBy,
    };
  },

  /**
   * Update tenant's pricing rules (weights)
   * @param {Object} metricWeights - Object mapping metric ID to weight (must sum to 100)
   */
  updatePricingRules: async (metricWeights) => {
    await simulateDelay();

    // Validate weights sum to 100
    const total = Object.values(metricWeights).reduce((sum, w) => sum + w, 0);
    if (Math.abs(total - 100) > 0.01) {
      throw new Error(`Weights must sum to 100, got ${total}`);
    }

    mockTenantPricingRules = {
      metricWeights: { ...metricWeights },
      updatedAt: new Date().toISOString(),
      updatedBy: 'current-user',
    };

    return {
      success: true,
      ...mockTenantPricingRules,
    };
  },

  /**
   * Reset tenant's pricing rules to industry defaults
   */
  resetPricingRules: async () => {
    await simulateDelay();

    const defaults = mockIndustryDefaultWeights.commercial_construction;
    mockTenantPricingRules = {
      metricWeights: { ...defaults.metricWeights },
      updatedAt: null,
      updatedBy: null,
    };

    return {
      success: true,
      ...mockTenantPricingRules,
    };
  },

  // Admin Pricing Rules API methods

  /**
   * Get industry default weights (admin view)
   * @param {string} industry - Industry identifier
   */
  getIndustryDefaults: async (industry = 'commercial_construction') => {
    await simulateDelay();

    const defaults = mockIndustryDefaultWeights[industry];
    if (!defaults) {
      throw new Error(`Industry not found: ${industry}`);
    }

    return {
      industry,
      availableMetrics: mockSimilarityMetrics,
      metricWeights: { ...defaults.metricWeights },
      updatedAt: defaults.updatedAt,
      updatedBy: defaults.updatedBy,
    };
  },

  /**
   * Update industry default weights (admin only)
   * @param {string} industry - Industry identifier
   * @param {Object} metricWeights - Object mapping metric ID to weight
   */
  updateIndustryDefaults: async (industry, metricWeights) => {
    await simulateDelay();

    // Validate weights sum to 100
    const total = Object.values(metricWeights).reduce((sum, w) => sum + w, 0);
    if (Math.abs(total - 100) > 0.01) {
      throw new Error(`Weights must sum to 100, got ${total}`);
    }

    mockIndustryDefaultWeights[industry] = {
      metricWeights: { ...metricWeights },
      updatedAt: new Date().toISOString(),
      updatedBy: 'admin',
    };

    return {
      success: true,
      industry,
      ...mockIndustryDefaultWeights[industry],
    };
  },

  /**
   * Get list of tenants with their custom weight status (admin view)
   */
  getTenantsWithCustomWeights: async () => {
    await simulateDelay();
    return {
      tenants: mockTenantsWithCustomWeights,
      total: mockTenantsWithCustomWeights.length,
    };
  },
};

// Simulate network delay for realistic feel
function simulateDelay(min = 200, max = 600) {
  const delay = Math.random() * (max - min) + min;
  return new Promise((resolve) => setTimeout(resolve, delay));
}

export default mockApiService;
